import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, differenceInMinutes, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = () => format(new Date(), 'yyyy-MM-dd');

function formatDuracion(minutos) {
  if (!minutos && minutos !== 0) return '—';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function calcularHorasTrabajadas(marcaciones) {
  // Parear entradas con salidas
  let totalMin = 0;
  const pares = [];
  let entrada = null;
  for (const m of marcaciones) {
    if (m.tipo === 'entrada') {
      entrada = m;
    } else if (m.tipo === 'salida' && entrada) {
      const min = differenceInMinutes(new Date(m.created_at), new Date(entrada.created_at));
      pares.push({ entrada, salida: m, minutos: min });
      totalMin += min;
      entrada = null;
    }
  }
  if (entrada) {
    // Aún activo
    const min = differenceInMinutes(new Date(), new Date(entrada.created_at));
    pares.push({ entrada, salida: null, minutos: min, activo: true });
    totalMin += min;
  }
  return { pares, totalMin };
}

// ─────────────────────────────────────────────
// VISTA EMPLEADO — Pantalla de marcación
// ─────────────────────────────────────────────
function VistaEmpleado({ usuario }) {
  const [estado, setEstado] = useState(null); // { marcaciones, ultima, activo }
  const [cargando, setCargando] = useState(true);
  const [marcando, setMarcando] = useState(false);
  const [tiempoActivo, setTiempoActivo] = useState(0); // minutos desde entrada
  const timerRef = useRef(null);

  const cargarEstado = useCallback(() => {
    API.get('/marcaciones/estado')
      .then(r => setEstado(r.data))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargarEstado(); }, []);

  // Timer para mostrar tiempo trabajado en tiempo real
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (estado?.activo && estado?.ultima) {
      const actualizar = () => {
        const min = differenceInMinutes(new Date(), new Date(estado.ultima.created_at));
        setTiempoActivo(min);
      };
      actualizar();
      timerRef.current = setInterval(actualizar, 60000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [estado]);

  const marcar = async () => {
    setMarcando(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('Tu dispositivo no soporta geolocalización'));
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, timeout: 10000, maximumAge: 0,
        });
      });
      const tipo = estado?.activo ? 'salida' : 'entrada';
      const r = await API.post('/marcaciones', {
        tipo,
        latitud: pos.coords.latitude,
        longitud: pos.coords.longitude,
      });
      toast.success(tipo === 'entrada'
        ? `✅ Entrada registrada — ${r.data.bodega}`
        : `👋 Salida registrada — ${r.data.bodega}`
      );
      cargarEstado();
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Error al marcar';
      toast.error(msg);
    } finally {
      setMarcando(false);
    }
  };

  const { pares, totalMin } = estado ? calcularHorasTrabajadas(estado.marcaciones) : { pares: [], totalMin: 0 };
  const activo = estado?.activo;

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header personal */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%', margin: '0 auto 12px',
          background: 'linear-gradient(135deg, #1a1a2e, #486b9b)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, color: 'white', fontWeight: 800,
        }}>
          {usuario?.nombre?.charAt(0).toUpperCase()}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>{usuario?.nombre}</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
          {format(new Date(), "EEEE d 'de' MMMM", { locale: es })}
        </div>
      </div>

      {/* Estado actual */}
      <div style={{
        background: activo ? 'linear-gradient(135deg, #064E3B, #065F46)' : 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: 20, padding: '24px 20px', textAlign: 'center', marginBottom: 24,
        boxShadow: activo ? '0 8px 32px rgba(6,78,59,0.4)' : '0 8px 32px rgba(26,26,46,0.4)',
        transition: 'all 0.4s',
      }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>
          {cargando ? '⏳' : activo ? '🟢' : '⚫'}
        </div>
        <div style={{ color: 'white', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
          {cargando ? 'Cargando...' : activo ? 'Trabajando' : 'Fuera de turno'}
        </div>
        {activo && estado?.ultima && (
          <>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              Desde las {format(new Date(estado.ultima.created_at), 'h:mm a')}
            </div>
            <div style={{ color: '#86EFAC', fontSize: 28, fontWeight: 800, marginTop: 8 }}>
              {formatDuracion(tiempoActivo)}
            </div>
          </>
        )}
        {!activo && totalMin > 0 && (
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 4 }}>
            Total hoy: <strong style={{ color: 'white' }}>{formatDuracion(totalMin)}</strong>
          </div>
        )}
      </div>

      {/* Botón principal */}
      <button
        onClick={marcar}
        disabled={marcando || cargando}
        style={{
          width: '100%', padding: '20px', border: 'none', borderRadius: 16,
          fontSize: 18, fontWeight: 800, cursor: marcando ? 'wait' : 'pointer',
          background: activo
            ? 'linear-gradient(135deg, #DC2626, #B91C1C)'
            : 'linear-gradient(135deg, #059669, #047857)',
          color: 'white',
          boxShadow: activo
            ? '0 6px 20px rgba(220,38,38,0.4)'
            : '0 6px 20px rgba(5,150,105,0.4)',
          opacity: (marcando || cargando) ? 0.7 : 1,
          transition: 'all 0.3s',
          marginBottom: 24,
        }}
      >
        {marcando ? '📍 Obteniendo ubicación...' : activo ? '🔴 Marcar Salida' : '🟢 Marcar Entrada'}
      </button>

      {/* Registro del día */}
      {pares.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, fontSize: 14, color: '#111827' }}>
            📋 Registro de hoy
          </div>
          {pares.map((p, i) => (
            <div key={i} style={{ padding: '12px 16px', borderBottom: i < pares.length - 1 ? '1px solid #F9FAFB' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  🟢 {format(new Date(p.entrada.created_at), 'h:mm a')}
                  {p.salida && <span style={{ color: '#6B7280' }}> → 🔴 {format(new Date(p.salida.created_at), 'h:mm a')}</span>}
                  {p.activo && <span style={{ color: '#059669' }}> → ahora</span>}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                  {p.entrada.bodega_nombre} · {p.entrada.distancia_metros}m del PDV
                </div>
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, color: p.activo ? '#059669' : '#1a1a2e' }}>
                {formatDuracion(p.minutos)}
              </div>
            </div>
          ))}
          <div style={{ padding: '10px 16px', background: '#F9FAFB', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: '#6B7280' }}>Total del día</span>
            <span style={{ fontWeight: 800, color: '#1a1a2e', fontSize: 15 }}>{formatDuracion(totalMin)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// VISTA ADMIN — Resumen de marcaciones
// ─────────────────────────────────────────────
function VistaAdmin() {
  const [marcaciones, setMarcaciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(hoy());
  const [fechaFin, setFechaFin] = useState(hoy());
  const [bodegaId, setBodegaId] = useState('');
  const [bodegas, setBodegas] = useState([]);
  const [tab, setTab] = useState('resumen'); // resumen | detalle

  useEffect(() => {
    API.get('/bodegas').then(r => setBodegas(r.data.filter(b => b.tipo === 'punto_venta'))).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    API.get('/marcaciones', { params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin, bodega_id: bodegaId || null } })
      .then(r => setMarcaciones(r.data))
      .catch(() => toast.error('Error cargando marcaciones'))
      .finally(() => setCargando(false));
  }, [fechaInicio, fechaFin, bodegaId]);

  useEffect(() => { cargar(); }, [fechaInicio, fechaFin, bodegaId]);

  // Agrupar por empleado para resumen
  const porEmpleado = {};
  marcaciones.forEach(m => {
    if (!porEmpleado[m.usuario_id]) {
      porEmpleado[m.usuario_id] = { nombre: m.usuario_nombre, bodega: m.bodega_nombre, marcaciones: [] };
    }
    porEmpleado[m.usuario_id].marcaciones.push(m);
  });

  // Calcular horas por empleado
  const resumen = Object.entries(porEmpleado).map(([uid, data]) => {
    const ordered = [...data.marcaciones].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const { pares, totalMin } = calcularHorasTrabajadas(ordered);
    return { uid, nombre: data.nombre, bodega: data.bodega, totalMin, pares, activo: pares.some(p => p.activo) };
  }).sort((a, b) => b.totalMin - a.totalMin);

  const totalGeneral = resumen.reduce((t, e) => t + e.totalMin, 0);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Filtros */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={st.label}>Desde</div>
          <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} style={st.input} />
        </div>
        <div>
          <div style={st.label}>Hasta</div>
          <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} style={st.input} />
        </div>
        <div>
          <div style={st.label}>Punto de venta</div>
          <select value={bodegaId} onChange={e => setBodegaId(e.target.value)} style={st.select}>
            <option value="">Todos</option>
            {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        {/* Accesos rápidos */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Hoy', fn: () => { setFechaInicio(hoy()); setFechaFin(hoy()); } },
            { label: 'Esta semana', fn: () => {
              const d = new Date();
              const lun = new Date(d); lun.setDate(d.getDate() - (d.getDay() || 7) + 1);
              setFechaInicio(format(lun, 'yyyy-MM-dd')); setFechaFin(hoy());
            }},
            { label: 'Este mes', fn: () => {
              const d = new Date();
              setFechaInicio(format(new Date(d.getFullYear(), d.getMonth(), 1), 'yyyy-MM-dd'));
              setFechaFin(hoy());
            }},
          ].map(({ label, fn }) => (
            <button key={label} onClick={fn} style={{ padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {label}
            </button>
          ))}
        </div>
        {/* Tabs */}
        <div style={{ marginLeft: 'auto', display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
          {[{ id: 'resumen', label: '📊 Resumen' }, { id: 'detalle', label: '📋 Detalle' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
              fontWeight: tab === t.id ? 700 : 500,
              background: tab === t.id ? 'white' : 'transparent',
              color: tab === t.id ? '#111827' : '#6B7280',
              boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Cargando...</div>
      ) : tab === 'resumen' ? (

        /* ── RESUMEN POR EMPLEADO ── */
        <>
          {/* Stats rápidas */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            {[
              { icon: '👥', label: 'Empleados', valor: resumen.length, color: '#3B82F6' },
              { icon: '🟢', label: 'Activos ahora', valor: resumen.filter(e => e.activo).length, color: '#10B981' },
              { icon: '⏱️', label: 'Horas totales', valor: formatDuracion(totalGeneral), color: '#8B5CF6' },
            ].map(s => (
              <div key={s.label} style={{ background: 'white', borderRadius: 12, padding: '14px 20px', flex: 1, minWidth: 120, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <div style={{ fontSize: 22 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.valor}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {resumen.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF', background: 'white', borderRadius: 14 }}>Sin marcaciones en el período</div>
          ) : (
            <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    {['Empleado', 'PDV', 'Estado', 'Entradas', 'Horas trabajadas'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resumen.map((e, i) => (
                    <tr key={e.uid} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#111827' }}>{e.nombre}</td>
                      <td style={{ padding: '12px 16px', color: '#6B7280' }}>{e.bodega || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, padding: '3px 9px', borderRadius: 20, fontWeight: 700,
                          background: e.activo ? '#D1FAE5' : '#F3F4F6',
                          color: e.activo ? '#065F46' : '#6B7280',
                        }}>
                          {e.activo ? '🟢 Activo' : '⚫ Inactivo'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#374151' }}>{e.pares.length}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: e.totalMin >= 480 ? '#059669' : e.totalMin >= 240 ? '#D97706' : '#374151' }}>
                          {formatDuracion(e.totalMin)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>

      ) : (

        /* ── DETALLE ── */
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          {marcaciones.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#9CA3AF' }}>Sin marcaciones en el período</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha/Hora', 'Empleado', 'PDV', 'Tipo', 'Distancia', 'Válida'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {marcaciones.map((m, i) => (
                  <tr key={m.id} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>
                      <div style={{ fontWeight: 600 }}>{format(new Date(m.created_at), "d MMM", { locale: es })}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{format(new Date(m.created_at), 'h:mm a')}</div>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{m.usuario_nombre}</td>
                    <td style={{ padding: '10px 14px', color: '#6B7280' }}>{m.bodega_nombre}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 20, fontWeight: 700,
                        background: m.tipo === 'entrada' ? '#D1FAE5' : '#FEE2E2',
                        color: m.tipo === 'entrada' ? '#065F46' : '#991B1B',
                      }}>
                        {m.tipo === 'entrada' ? '🟢 Entrada' : '🔴 Salida'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6B7280' }}>{m.distancia_metros}m</td>
                    <td style={{ padding: '10px 14px' }}>
                      {m.valida
                        ? <span style={{ color: '#059669', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: '#DC2626', fontWeight: 700 }}>✗</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const st = {
  label:  { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 },
  input:  { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' },
  select: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
export default function Marcaciones() {
  const { usuario } = useAuth();
  const esEmpleado = usuario?.rol === 'empleado';
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: esEmpleado ? 480 : 1100, margin: '0 auto' }}>
      {!esEmpleado && (
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>⏱️ Marcaciones</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Control de asistencia y horas trabajadas</p>
        </div>
      )}
      {esEmpleado && <VistaEmpleado usuario={usuario} />}
      {esAdmin && <VistaAdmin />}
      {!esEmpleado && !esAdmin && (
        <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Sin acceso</div>
      )}
    </div>
  );
}
